import RadioButton from "../../UI/RadioButton";
import Button from "../../UI/Button";
import IconButton from "../../UI/IconButton";
import ApiErrorMessages from "../../UI/ApiErrorMessages";
import ErrorMessages from "../../UI/ErrorMessages";
import { getReportTypeOptions } from "../../../utils/radiobuttonHelpers";
import getClassNames from "../../../utils/getClassNames";
import { REPORT_TYPE_NAME_MAP } from "../../../constants/reportTypes";
import { mapping } from "../steps/stepMapping";
import { useReportBuilder } from "../../../hooks/reports/useReportBuilder";
import { useParams } from "react-router-dom";

interface ReportCriteriaProps {
  onRunReport: (mode: "new" | "draft") => void;
  isRunning: boolean;
  runError?: any;
  criteriaExpanded: boolean;
  setCriteriaExpanded: (expanded: boolean) => void;
  reportTypeExpanded: boolean;
  setReportTypeExpanded: (expanded: boolean) => void;
  isLocked: boolean;
}

const ReportCriteria = ({
  onRunReport,
  isRunning,
  runError,
  criteriaExpanded,
  setCriteriaExpanded,
  reportTypeExpanded,
  setReportTypeExpanded,
  isLocked,
}: ReportCriteriaProps) => {
  const { reportId } = useParams<{ reportId: string }>();
  // custom hooks
  const builder = useReportBuilder();

  const showRunButton = builder.showRunReportOptions && (!reportId || builder.isDirty);

  return (
    <div
      className={getClassNames(
        "report-criteria",
        criteriaExpanded ? "expanded" : "",
      )}
    >
      <div className="report-criteria-header">
        <div className="row justify-content-between align-items-center">
          <div className="column-auto">
            <h2>Criteria</h2>
          </div>
          <div className="column-1">
            <IconButton
              id="toggle-criteria-expansion"
              className="small flat"
              icon={
                <i
                  className={
                    criteriaExpanded
                      ? "ri-arrow-up-s-line"
                      : "ri-arrow-down-s-line"
                  }
                />
              }
              onClick={() => setCriteriaExpanded(!criteriaExpanded)}
            />
          </div>
        </div>
      </div>

      <div className="report-criteria-body">
        <div className="report-steps">
          <ApiErrorMessages error={runError} />
          {builder.showRunReportOptions && (
            <ErrorMessages
              messages={builder.steps.map((s) => s.validation).flat()}
            />
          )}

          {/* Report Type Selection */}
          <div
            className={getClassNames(
              "report-step",
              reportTypeExpanded ? "expanded" : "",
            )}
          >
            <div className="report-step-header">
              <div className="row justify-content-between align-items-center">
                <div className="column-auto">
                  <h3>
                    What do you want to see?
                    {builder.reportType
                      ? ` - ${REPORT_TYPE_NAME_MAP[builder.reportType]}`
                      : ""}
                  </h3>
                </div>
                <div className="column-1">
                  <IconButton
                    id="toggle-report-type-expansion"
                    className="small flat"
                    icon={
                      <i
                        className={
                          reportTypeExpanded
                            ? "ri-arrow-up-s-line"
                            : "ri-arrow-down-s-line"
                        }
                      />
                    }
                    onClick={() => setReportTypeExpanded(!reportTypeExpanded)}
                  />
                </div>
              </div>
            </div>
            <div className="report-step-body">
              <div className="row margin-bottom-3">
                <div className="column">
                  <RadioButton
                    id="report-type"
                    disabled={isRunning || isLocked || !!reportId}
                    options={getReportTypeOptions()}
                    onChange={reportId ? undefined : builder.updateReportType}
                    value={builder.reportType}
                  />
                </div>
              </div>
              {!reportId && (
                <div className="row justify-content-end margin-bottom-3">
                  <div className="column-auto">
                    <Button
                      id="report-type-next"
                      text="Next"
                      icon={<i className="ri-arrow-right-s-line" />}
                      iconLocation="end"
                      className="secondary"
                      onClick={() => {
                        setReportTypeExpanded(false);
                        builder.startWorkflow();
                      }}
                      disabled={!builder.reportType || isLocked}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Steps */}
          {builder.steps.map((s) => {
            const Step = mapping[s.step];
            return (
              <Step
                key={s.step}
                values={s.values}
                expanded={s.expanded}
                reportType={builder.reportType}
                allStepValues={builder.getAllStepValues()}
                disabled={s.disabled || isRunning || isLocked}
                onExpand={builder.handleOnStepExpanded}
                onNext={builder.handleNextStep}
                onChange={builder.updateStepValues}
              />
            );
          })}

          {showRunButton && (
            <>
              <div className="row justify-content-end padding-2">
                <div className="column-auto">
                  <Button
                    id="submit-report"
                    text="Run Report"
                    loading={isRunning}
                    onClick={() =>
                      onRunReport(reportId && builder.isDirty ? "draft" : "new")
                    }
                    disabled={
                      builder.steps.some((s) => s.validation.length !== 0) ||
                      isRunning ||
                      isLocked
                    }
                  />
                </div>
              </div>
              {builder.steps.some((s) => s.validation.length !== 0) && (
                <div className="row justify-content-end padding-2">
                  <div className="column-auto">
                    <ErrorMessages
                      messages={[
                        "Unable to submit, please check your steps for validation errors",
                      ]}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportCriteria;
